// role-permissions.ts
// Matrice des permissions par rôle, conforme au document ROLES.md.
// Source de vérité pour les contrôles d'accès côté front.

import type { UserRole } from "@/domain/enums/user-role";

/** Niveau d'accès à une fonctionnalité */
export type FeatureAccess = "modification" | "consultation" | false;

export interface RolePermissions {
  /** Périmètre de portée par défaut pour ce rôle */
  defaultScope: "National" | "Region" | "Site";

  /** Section administration (SystemAdmin uniquement pour la gestion) */
  admin: {
    regions: FeatureAccess;
    sites: FeatureAccess;
    workUnits: FeatureAccess;
    users: FeatureAccess;
    auditLog: FeatureAccess;
    importData: FeatureAccess;
    powerBiExport: boolean;
  };

  /** Section DUERP site */
  duerp: {
    nationalDashboard: FeatureAccess;
    regionDashboard: FeatureAccess;
    /** Accès au DUERP d'un site (CONSULTATION ou MODIFICATION) */
    sites: FeatureAccess;
    /** Geler / valider une version et créer une nouvelle version DUERP */
    freezeVersion: boolean;
    addRisk: boolean;
    exportPdf: boolean;
    riskMatrix: FeatureAccess;
    editRisk: boolean;
    exportRiskPdf: boolean;
  };
}

/**
 * Permissions de chaque rôle applicatif.
 * SystemAdmin a accès total ; il n'apparaît pas ici car le contrôle
 * court-circuite la matrice dans usePermissions.
 */
export const ROLE_PERMISSIONS: Record<UserRole, RolePermissions> = {
  SystemAdmin: {
    defaultScope: "National",
    admin: {
      regions: "modification",
      sites: "modification",
      workUnits: "modification",
      users: "modification",
      auditLog: "modification",
      importData: "modification",
      powerBiExport: true,
    },
    duerp: {
      nationalDashboard: "modification",
      regionDashboard: "modification",
      sites: "modification",
      freezeVersion: true,
      addRisk: true,
      exportPdf: true,
      riskMatrix: "modification",
      editRisk: true,
      exportRiskPdf: true,
    },
  },

  // --- Rôles national ---

  ResponsableHse: {
    defaultScope: "National",
    admin: {
      regions: "modification",
      sites: "modification",
      workUnits: "modification",
      users: "modification",
      auditLog: "modification",
      importData: false,
      powerBiExport: true,
    },
    duerp: {
      nationalDashboard: "consultation",
      regionDashboard: "consultation",
      sites: "modification",
      freezeVersion: false,
      addRisk: true,
      exportPdf: true,
      riskMatrix: "consultation",
      editRisk: true,
      exportRiskPdf: true,
    },
  },

  HsedgSpecialist: {
    defaultScope: "National",
    admin: {
      regions: "modification",
      sites: "modification",
      workUnits: "modification",
      users: "modification",
      auditLog: "modification",
      importData: false,
      powerBiExport: true,
    },
    duerp: {
      nationalDashboard: "consultation",
      regionDashboard: "consultation",
      sites: "modification",
      freezeVersion: true,
      addRisk: true,
      exportPdf: true,
      riskMatrix: "consultation",
      editRisk: true,
      exportRiskPdf: true,
    },
  },

  LaborRelation: {
    defaultScope: "National",
    admin: {
      regions: false,
      sites: false,
      workUnits: false,
      users: false,
      auditLog: false,
      importData: false,
      powerBiExport: false,
    },
    duerp: {
      nationalDashboard: "consultation",
      regionDashboard: "consultation",
      sites: "consultation",
      freezeVersion: false,
      addRisk: false,
      exportPdf: true,
      riskMatrix: "consultation",
      editRisk: false,
      exportRiskPdf: true,
    },
  },

  HrService: {
    defaultScope: "National",
    admin: {
      regions: false,
      sites: false,
      workUnits: false,
      users: false,
      auditLog: false,
      importData: false,
      powerBiExport: false,
    },
    duerp: {
      nationalDashboard: "consultation",
      regionDashboard: "consultation",
      sites: "consultation",
      freezeVersion: false,
      addRisk: false,
      exportPdf: true,
      riskMatrix: "consultation",
      editRisk: false,
      exportRiskPdf: true,
    },
  },

  ManagingDirector: {
    defaultScope: "National",
    admin: {
      regions: false,
      sites: false,
      workUnits: false,
      users: false,
      auditLog: false,
      importData: false,
      powerBiExport: false,
    },
    duerp: {
      nationalDashboard: "consultation",
      regionDashboard: "consultation",
      sites: "consultation",
      freezeVersion: false,
      addRisk: false,
      exportPdf: false,
      riskMatrix: "consultation",
      editRisk: false,
      exportRiskPdf: false,
    },
  },

  // --- Rôle régional ---

  SeniorManager: {
    defaultScope: "Region",
    admin: {
      regions: false,
      sites: false,
      workUnits: false,
      users: false,
      auditLog: false,
      importData: false,
      powerBiExport: false,
    },
    duerp: {
      nationalDashboard: false,
      regionDashboard: "consultation",
      sites: "consultation",
      freezeVersion: false,
      addRisk: false,
      exportPdf: true,
      riskMatrix: "consultation",
      editRisk: false,
      exportRiskPdf: true,
    },
  },

  // --- Rôles site ---

  Deputy: {
    defaultScope: "Site",
    admin: {
      regions: false,
      sites: false,
      workUnits: false,
      users: false,
      auditLog: false,
      importData: false,
      powerBiExport: false,
    },
    duerp: {
      nationalDashboard: false,
      regionDashboard: false,
      sites: "modification",
      freezeVersion: false,
      addRisk: false,
      exportPdf: true,
      riskMatrix: "consultation",
      editRisk: false,
      exportRiskPdf: false,
    },
  },

  StationManager: {
    defaultScope: "Site",
    admin: {
      regions: false,
      sites: false,
      workUnits: false,
      users: false,
      auditLog: false,
      importData: false,
      powerBiExport: false,
    },
    duerp: {
      nationalDashboard: false,
      regionDashboard: false,
      sites: "modification",
      freezeVersion: false,
      addRisk: true,
      exportPdf: true,
      riskMatrix: "consultation",
      editRisk: true,
      exportRiskPdf: true,
    },
  },
};

/** Labels français des rôles (affichage UI) */
export const ROLE_LABELS: Record<UserRole, string> = {
  SystemAdmin: "Administrateur système",
  ResponsableHse: "Manager H&S",
  HsedgSpecialist: "HSEDG Specialist",
  LaborRelation: "Labor Relations",
  Deputy: "Délégué du personnel",
  HrService: "HR Services",
  StationManager: "Station manager",
  SeniorManager: "Senior manager",
  ManagingDirector: "Managing director",
};

/**
 * Rôles assignables via l'interface d'administration.
 * SystemAdmin est exclu : il ne peut pas être attribué manuellement.
 */
export const ASSIGNABLE_ROLES: UserRole[] = [
  "ResponsableHse",
  "HsedgSpecialist",
  "LaborRelation",
  "Deputy",
  "HrService",
  "StationManager",
  "SeniorManager",
  "ManagingDirector",
];

/**
 * Derives the default ScopeLevel implied by a role.
 * Source of truth: Frontend Guide — Role & Permission Assignment.
 */
export function getScopeForRole(
  role: UserRole,
): "National" | "Region" | "Site" {
  return ROLE_PERMISSIONS[role].defaultScope;
}

/** Roles with national defaults that may also carry a data restriction. */
export function roleSupportsDataRestriction(role: UserRole): boolean {
  return role === "HsedgSpecialist" || role === "ManagingDirector";
}

/**
 * Returns true for roles that may require a POST /permissions call after
 * PATCH /users/:id.
 */
export function roleRequiresPermission(role: UserRole): boolean {
  if (roleSupportsDataRestriction(role)) return true;
  const scope = getScopeForRole(role);
  return scope === "Region" || scope === "Site";
}
